import { useSyncExternalStore } from 'react';
import type { ComposeDraftSummary } from '../../api/types';

/**
 * The new messages this agent has started and not sent.
 *
 * The record of truth is the SERVER (`compose_drafts`, one row per draft — see
 * the migration for why these cannot live in `message_drafts`). This module is
 * the one thing a server cannot be: a live picture of what the DRAFTS VIEW
 * should be showing, updated as the panel saves rather than on the next list
 * read. Without it, discarding a draft leaves its row on screen until the agent
 * changes view, and the first save of a new one does not appear at all.
 *
 * Deliberately NOT the localStorage buffer its reply-side sibling keeps
 * (`drafts.ts`). That buffer exists because the reply composer's save is
 * debounced and a thread switch must not eat a sentence; the compose window
 * cannot be switched away from — it is a dock that stays mounted — and it
 * cannot be closed without answering "save or discard", so the sentence has a
 * way out that does not depend on this browser.
 *
 * Held per module rather than per tenant, and it can therefore hold a moment of
 * another tenant's rows after a switch — the same posture `drafts.ts` takes,
 * and safe for the same reason: every list read replaces the lot, and a stale id
 * here is only a row that vanishes, never a row read as one of this tenant's
 * (`GET /compose_drafts/:id` is tenant-scoped, so clicking one opens nothing).
 */

/** Newest edit first — the order the Drafts view reads in ("what was I in the middle of"). */
export function sortComposeDrafts(rows: readonly ComposeDraftSummary[]): ComposeDraftSummary[] {
  return [...rows].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

/**
 * How a row describes itself: the recipient's name, or the raw address typed
 * for somebody who is not in the CRM yet. Never blank — a draft with nothing to
 * call its recipient is one an agent cannot recognise in a list.
 */
export function composeDraftRecipient(draft: ComposeDraftSummary): string {
  if (draft.contact) {
    return (
      draft.contact.name || draft.contact.email || draft.contact.phoneNumber || 'Unknown recipient'
    );
  }
  return draft.toAddress || 'Unknown recipient';
}

/**
 * The one line under the name. The subject leads on the channels that have one
 * — an email whose body is still empty is still recognisable by it — and the
 * body follows. A draft with neither says so rather than rendering an empty
 * row, since attachments alone are a real draft.
 */
export function composeDraftSnippet(draft: ComposeDraftSummary): string {
  const parts = [draft.subject?.trim(), draft.preview.trim()].filter(Boolean);
  if (parts.length) return parts.join(' — ');
  if (draft.attachmentCount > 0) {
    return `${draft.attachmentCount} attachment${draft.attachmentCount > 1 ? 's' : ''}`;
  }
  return 'No message yet';
}

/**
 * Does this draft answer the list's search box?
 *
 * Client-side, and it has to be: there is no server-side search for compose
 * drafts, and the alternative — dropping them from the list the moment anybody
 * types — hides exactly the unsent message somebody is searching the Drafts
 * view for. Matches what the ROW shows (the recipient, the subject, the
 * preview), because a hit on something invisible reads as a bug.
 */
export function matchesComposeDraft(draft: ComposeDraftSummary, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const hay = [
    composeDraftRecipient(draft),
    draft.contact?.email,
    draft.contact?.phoneNumber,
    draft.toAddress,
    draft.subject,
    draft.preview,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (hay.includes(needle)) return true;
  // A number typed with spaces or brackets must still find a number stored
  // without them — the same courtesy the conversation search extends.
  const digits = needle.replace(/[^0-9]/g, '');
  return digits.length >= 3 && hay.replace(/[^0-9]/g, '').includes(digits);
}

let drafts: readonly ComposeDraftSummary[] = [];
const listeners = new Set<() => void>();

function publish(next: readonly ComposeDraftSummary[]) {
  drafts = next;
  for (const listener of listeners) listener();
}

/** Take a server list read wholesale — it is the authority on what exists. */
export function seedComposeDrafts(rows: ComposeDraftSummary[]): void {
  publish(sortComposeDrafts(rows));
}

/** A save landed: this draft is now this. Inserts it if the list had not seen it. */
export function upsertComposeDraft(draft: ComposeDraftSummary): void {
  publish(sortComposeDrafts([...drafts.filter((row) => row.id !== draft.id), draft]));
}

/** Discarded, sent, or emptied to nothing — all three leave no row. */
export function dropComposeDraft(id: number): void {
  if (!drafts.some((row) => row.id === id)) return;
  publish(drafts.filter((row) => row.id !== id));
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * What the store holds right now. Exported because it is also the only way to
 * read it without a React render — the hook below is the same function.
 */
export const composeDraftsSnapshot = (): readonly ComposeDraftSummary[] => drafts;
const snapshot = composeDraftsSnapshot;

/** Every compose draft this agent holds, newest edit first. */
export function useComposeDrafts(): readonly ComposeDraftSummary[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
